/**
 * Day 15: Centre-Based Full-Day Session Foundation
 * 
 * Admin page for managing centre-based group programs:
 * - Session CRUD (container for activities + participants + staff)
 * - Session participants management
 * - Session staff assignment
 * - Activity blocks within sessions
 * - Activity-participant linking
 * - Session-to-shift bridge layer
 */

import React, { useState, useEffect, useCallback } from 'react';
import LineItemSelect from '@/components/common/LineItemSelect';   // client#19: search line items by name or code
import { Card, CardHeader, CardTitle, CardContent, CardDescription, CardFooter } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../../components/ui/tabs';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '../../components/ui/sheet';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../../components/ui/table';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Separator } from '../../components/ui/separator';
import { toast } from 'sonner';
import { notify } from '@/lib/notifications/engine';
import {
  Calendar,
  Clock,
  Users,
  UserPlus,
  UserMinus,
  Plus,
  Edit,
  Trash2,
  Play,
  CheckCircle,
  XCircle,
  ArrowRight,
  Activity,
  Building2,
  RefreshCw,
  Link2,
  Unlink,
  AlertCircle,
  Info,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

// Status badge colors
const STATUS_COLORS = {
  draft: 'bg-slate-500',
  scheduled: 'bg-blue-500',
  active: 'bg-green-500',
  completed: 'bg-purple-500',
  cancelled: 'bg-red-500',
};

// Participant status colors
const PARTICIPANT_STATUS_COLORS = {
  registered: 'bg-blue-500',
  attended: 'bg-green-500',
  absent: 'bg-amber-500',
  cancelled: 'bg-red-500',
};

// Staff role colors
const ROLE_COLORS = {
  support_worker: 'bg-blue-500',
  lead: 'bg-purple-500',
  assistant: 'bg-teal-500',
};

export default function CentreSessionsPage() {
  const [sessions, setSessions] = useState([]);
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selectedSession, setSelectedSession] = useState(null);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [showDetailSheet, setShowDetailSheet] = useState(false);
  const [activeTab, setActiveTab] = useState('all');
  const [dateFilter, setDateFilter] = useState('');
  
  // Detail sheet state
  const [sessionDetails, setSessionDetails] = useState(null);
  const [detailTab, setDetailTab] = useState('overview');
  const [loadingDetails, setLoadingDetails] = useState(false);
  
  // Dialog states
  const [showAddParticipant, setShowAddParticipant] = useState(false);
  const [showAddStaff, setShowAddStaff] = useState(false);
  const [showAddActivity, setShowAddActivity] = useState(false);
  const [showCreateShifts, setShowCreateShifts] = useState(false);
  
  // Form state
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    session_date: '',
    start_time: '09:00',
    end_time: '15:00',
    location_name: '',
    max_participants: 10,
    required_staff: 2,
    default_ratio: '1:3',
  });
  
  // Participant/staff lookups
  const [availableParticipants, setAvailableParticipants] = useState([]);
  const [availableStaff, setAvailableStaff] = useState([]);
  const [services, setServices] = useState([]);

  const getAuthHeaders = useCallback(() => {
    const token = localStorage.getItem('access_token');
    return {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${token}`,
    };
  }, []);

  // Fetch config
  const fetchConfig = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/config`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setConfig(data);
      }
    } catch (err) {
      console.error('Failed to fetch config:', err);
    }
  }, [getAuthHeaders]);

  // Fetch sessions
  const fetchSessions = useCallback(async () => {
    setLoading(true);
    try {
      let url = `${API_URL}/api/centre-sessions`;
      const params = new URLSearchParams();
      if (dateFilter) params.append('date_from', dateFilter);
      if (activeTab !== 'all') params.append('status', activeTab);
      if (params.toString()) url += `?${params.toString()}`;
      
      const res = await fetch(url, { headers: getAuthHeaders() });
      if (res.ok) {
        const data = await res.json();
        setSessions(data.items || []);
      } else if (res.status === 403) {
        // Module not enabled
        setConfig({ enabled: false });
      }
    } catch (err) {
      console.error('Failed to fetch sessions:', err);
      notify.error(e, 'CENTRE_SESSIONS_LOAD_FAILED');
    } finally {
      setLoading(false);
    }
  }, [getAuthHeaders, dateFilter, activeTab]);

  // Fetch lookups
  const fetchLookups = useCallback(async () => {
    try {
      const [participantsRes, staffRes, servicesRes] = await Promise.all([
        fetch(`${API_URL}/api/participants?limit=200`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/staff?limit=200`, { headers: getAuthHeaders() }),
        fetch(`${API_URL}/api/services/catalog?limit=100&mode=selection`, { headers: getAuthHeaders() }),
      ]);
      
      if (participantsRes.ok) {
        const data = await participantsRes.json();
        setAvailableParticipants(data.items || data.data || []);
      }
      if (staffRes.ok) {
        const data = await staffRes.json();
        setAvailableStaff(data.items || data.data || []);
      }
      if (servicesRes.ok) {
        const data = await servicesRes.json();
        setServices(data.items || data.data || []);
      }
    } catch (err) {
      console.error('Failed to fetch lookups:', err);
    }
  }, [getAuthHeaders]);

  // Fetch session details
  const fetchSessionDetails = useCallback(async (sessionId) => {
    setLoadingDetails(true);
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${sessionId}/full`, {
        headers: getAuthHeaders(),
      });
      if (res.ok) {
        const data = await res.json();
        setSessionDetails(data.data);
      }
    } catch (err) {
      console.error('Failed to fetch session details:', err);
      notify.error(e, 'CENTRE_SESSION_DETAIL_FAILED');
    } finally {
      setLoadingDetails(false);
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchConfig();
    fetchSessions();
    fetchLookups();
  }, [fetchConfig, fetchSessions, fetchLookups]);

  // Create session
  const handleCreateSession = async () => {
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(formData),
      });
      
      if (res.ok) {
        toast.success('Session created successfully');
        setShowCreateDialog(false);
        setFormData({
          name: '',
          description: '',
          session_date: '',
          start_time: '09:00',
          end_time: '15:00',
          location_name: '',
          max_participants: 10,
          required_staff: 2,
          default_ratio: '1:3',
        });
        fetchSessions();
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_SESSION_CREATE_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_SESSION_CREATE_FAILED');
    }
  };

  // Status transitions
  const handleStatusTransition = async (sessionId, action) => {
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${sessionId}/${action}`, {
        method: 'POST',
        headers: getAuthHeaders(),
      });
      
      if (res.ok) {
        toast.success(`Session ${action}d successfully`);
        fetchSessions();
        if (selectedSession?.session?.id === sessionId) {
          fetchSessionDetails(sessionId);
        }
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_SESSION_ACTION_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_SESSION_ACTION_FAILED');
    }
  };

  // Add participant
  const handleAddParticipant = async (participantId) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${selectedSession.session.id}/participants`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ participant_id: participantId }),
      });
      
      if (res.ok) {
        toast.success('Participant added');
        fetchSessionDetails(selectedSession.session.id);
        setShowAddParticipant(false);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_PARTICIPANT_ADD_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_PARTICIPANT_ADD_FAILED');
    }
  };

  // Remove participant
  const handleRemoveParticipant = async (participantId) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(
        `${API_URL}/api/centre-sessions/${selectedSession.session.id}/participants/${participantId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        toast.success('Participant removed');
        fetchSessionDetails(selectedSession.session.id);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_PARTICIPANT_REMOVE_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_PARTICIPANT_REMOVE_FAILED');
    }
  };

  // Add staff
  const handleAddStaff = async (staffId, role) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${selectedSession.session.id}/staff`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ staff_id: staffId, role }),
      });
      
      if (res.ok) {
        toast.success('Staff assigned');
        fetchSessionDetails(selectedSession.session.id);
        setShowAddStaff(false);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_STAFF_ASSIGN_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_STAFF_ASSIGN_FAILED');
    }
  };

  // Remove staff
  const handleRemoveStaff = async (staffId) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(
        `${API_URL}/api/centre-sessions/${selectedSession.session.id}/staff/${staffId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        toast.success('Staff removed');
        fetchSessionDetails(selectedSession.session.id);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_STAFF_REMOVE_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_STAFF_REMOVE_FAILED');
    }
  };

  // Add activity
  const [activityForm, setActivityForm] = useState({
    name: '',
    description: '',
    start_time: '',
    end_time: '',
    required_ratio: '1:3',
    location: '',
  });

  const handleAddActivity = async () => {
    if (!selectedSession) return;
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${selectedSession.session.id}/activities`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify(activityForm),
      });
      
      if (res.ok) {
        toast.success('Activity added');
        fetchSessionDetails(selectedSession.session.id);
        setShowAddActivity(false);
        setActivityForm({
          name: '',
          description: '',
          start_time: '',
          end_time: '',
          required_ratio: '1:3',
          location: '',
        });
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_ACTIVITY_ADD_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_ACTIVITY_ADD_FAILED');
    }
  };

  // Delete activity
  const handleDeleteActivity = async (activityId) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(
        `${API_URL}/api/centre-sessions/${selectedSession.session.id}/activities/${activityId}`,
        { method: 'DELETE', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        toast.success('Activity deleted');
        fetchSessionDetails(selectedSession.session.id);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_ACTIVITY_DELETE_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_ACTIVITY_DELETE_FAILED');
    }
  };

  // Link all participants to activity
  const handleLinkAllToActivity = async (activityId) => {
    if (!selectedSession) return;
    try {
      const res = await fetch(
        `${API_URL}/api/centre-sessions/${selectedSession.session.id}/activities/${activityId}/link-all-participants`,
        { method: 'POST', headers: getAuthHeaders() }
      );
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.linked_count} participants linked`);
        fetchSessionDetails(selectedSession.session.id);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_LINK_PARTICIPANTS_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_LINK_PARTICIPANTS_FAILED');
    }
  };

  // Create shifts from session
  const [selectedServiceId, setSelectedServiceId] = useState('');

  const handleCreateShifts = async () => {
    if (!selectedSession || !selectedServiceId) return;
    try {
      const res = await fetch(`${API_URL}/api/centre-sessions/${selectedSession.session.id}/create-shifts`, {
        method: 'POST',
        headers: getAuthHeaders(),
        body: JSON.stringify({ service_id: selectedServiceId }),
      });
      
      if (res.ok) {
        const data = await res.json();
        toast.success(`${data.count} shifts created`);
        fetchSessionDetails(selectedSession.session.id);
        setShowCreateShifts(false);
      } else {
        const err = await res.json();
        notify.error(err, 'CENTRE_SHIFTS_CREATE_FAILED');
      }
    } catch (err) {
      notify.error(e, 'CENTRE_SHIFTS_CREATE_FAILED');
    }
  };

  // Open session details
  const openSessionDetails = (session) => {
    setSelectedSession({ session });
    fetchSessionDetails(session.id);
    setShowDetailSheet(true);
    setDetailTab('overview');
  };

  // Module not enabled view
  if (config && !config.enabled) {
    return (
      <div className="p-6" data-testid="centre-sessions-disabled">
        <Card className="border-amber-200 bg-amber-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-amber-700">
              <AlertCircle className="h-5 w-5" />
              Centre Sessions Module Not Enabled
            </CardTitle>
          </CardHeader>
          <CardContent>
            <p className="text-amber-600">
              The Centre Sessions module is not enabled for your organisation.
              Contact your administrator to enable this feature.
            </p>
            <p className="text-sm text-amber-500 mt-2">
              Feature flag: <code className="bg-amber-100 px-1 rounded">centre_session_module_enabled</code>
            </p>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6" data-testid="centre-sessions-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Centre Sessions</h1>
          <p className="text-muted-foreground">
            Manage centre-based group programs and full-day sessions
          </p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={() => { fetchSessions(); fetchLookups(); }}>
            <RefreshCw className="h-4 w-4 mr-2" />
            Refresh
          </Button>
          <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
            <DialogTrigger asChild>
              <Button data-testid="create-session-btn">
                <Plus className="h-4 w-4 mr-2" />
                New Session
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-md">
              <DialogHeader>
                <DialogTitle>Create Centre Session</DialogTitle>
                <DialogDescription>
                  Create a new group session with activities and participants
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4 py-4">
                <div className="space-y-2">
                  <Label>Session Name *</Label>
                  <Input
                    data-testid="session-name-input"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="e.g., Morning Group Program"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Description</Label>
                  <Textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Optional description"
                  />
                </div>
                <div className="space-y-2">
                  <Label>Session Date *</Label>
                  <Input
                    type="date"
                    data-testid="session-date-input"
                    value={formData.session_date}
                    onChange={(e) => setFormData({ ...formData, session_date: e.target.value })}
                  />
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Start Time</Label>
                    <Input
                      type="time"
                      value={formData.start_time}
                      onChange={(e) => setFormData({ ...formData, start_time: e.target.value })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>End Time</Label>
                    <Input
                      type="time"
                      value={formData.end_time}
                      onChange={(e) => setFormData({ ...formData, end_time: e.target.value })}
                    />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Location</Label>
                  <Input
                    value={formData.location_name}
                    onChange={(e) => setFormData({ ...formData, location_name: e.target.value })}
                    placeholder="e.g., Community Centre"
                  />
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label>Max Participants</Label>
                    <Input
                      type="number"
                      min={1}
                      value={formData.max_participants}
                      onChange={(e) => setFormData({ ...formData, max_participants: parseInt(e.target.value) || 10 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Required Staff</Label>
                    <Input
                      type="number"
                      min={1}
                      value={formData.required_staff}
                      onChange={(e) => setFormData({ ...formData, required_staff: parseInt(e.target.value) || 2 })}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Default Ratio</Label>
                    <Select
                      value={formData.default_ratio}
                      onValueChange={(v) => setFormData({ ...formData, default_ratio: v })}
                    >
                      <SelectTrigger>
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {['1:1', '1:2', '1:3', '1:4', '1:5'].map((r) => (
                          <SelectItem key={r} value={r}>{r}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setShowCreateDialog(false)}>
                  Cancel
                </Button>
                <Button
                  data-testid="submit-create-session"
                  onClick={handleCreateSession}
                  disabled={!formData.name || !formData.session_date}
                >
                  Create Session
                </Button>
              </DialogFooter>
            </DialogContent>
          </Dialog>
        </div>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center gap-4">
            <div className="space-y-1">
              <Label className="text-xs text-muted-foreground">Date From</Label>
              <Input
                type="date"
                value={dateFilter}
                onChange={(e) => setDateFilter(e.target.value)}
                className="w-40"
              />
            </div>
            <Tabs value={activeTab} onValueChange={setActiveTab}>
              <TabsList>
                <TabsTrigger value="all">All</TabsTrigger>
                <TabsTrigger value="draft">Draft</TabsTrigger>
                <TabsTrigger value="scheduled">Scheduled</TabsTrigger>
                <TabsTrigger value="active">Active</TabsTrigger>
                <TabsTrigger value="completed">Completed</TabsTrigger>
              </TabsList>
            </Tabs>
          </div>
        </CardContent>
      </Card>

      {/* Sessions Table */}
      <Card>
        <CardHeader>
          <CardTitle>Sessions ({sessions.length})</CardTitle>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8 text-muted-foreground">Loading...</div>
          ) : sessions.length === 0 ? (
            <div className="text-center py-8 text-muted-foreground">
              No sessions found. Create your first session to get started.
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Session</TableHead>
                  <TableHead>Date</TableHead>
                  <TableHead>Time</TableHead>
                  <TableHead>Location</TableHead>
                  <TableHead>Participants</TableHead>
                  <TableHead>Staff</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead>Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {sessions.map((session) => (
                  <TableRow 
                    key={session.id} 
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => openSessionDetails(session)}
                    data-testid={`session-row-${session.id}`}
                  >
                    <TableCell>
                      <div className="font-medium">{session.name}</div>
                      {session.description && (
                        <div className="text-xs text-muted-foreground truncate max-w-[200px]">
                          {session.description}
                        </div>
                      )}
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Calendar className="h-3 w-3 text-muted-foreground" />
                        {session.session_date}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1">
                        <Clock className="h-3 w-3 text-muted-foreground" />
                        {session.start_time} - {session.end_time}
                      </div>
                    </TableCell>
                    <TableCell>
                      {session.location_name || '-'}
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <Users className="h-3 w-3" />
                        {session.participant_count || 0}/{session.max_participants}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="flex items-center gap-1 w-fit">
                        <UserPlus className="h-3 w-3" />
                        {session.staff_count || 0}/{session.required_staff}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={`${STATUS_COLORS[session.status]} text-white`}>
                        {session.status}
                      </Badge>
                    </TableCell>
                    <TableCell onClick={(e) => e.stopPropagation()}>
                      <div className="flex items-center gap-1">
                        {session.status === 'draft' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStatusTransition(session.id, 'schedule')}
                            title="Schedule"
                          >
                            <ArrowRight className="h-4 w-4" />
                          </Button>
                        )}
                        {session.status === 'scheduled' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStatusTransition(session.id, 'start')}
                            title="Start"
                          >
                            <Play className="h-4 w-4" />
                          </Button>
                        )}
                        {session.status === 'active' && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleStatusTransition(session.id, 'complete')}
                            title="Complete"
                          >
                            <CheckCircle className="h-4 w-4" />
                          </Button>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Session Detail Sheet */}
      <Sheet open={showDetailSheet} onOpenChange={setShowDetailSheet}>
        <SheetContent className="w-full sm:max-w-2xl overflow-y-auto">
          <SheetHeader>
            <SheetTitle>
              {sessionDetails?.session?.name || 'Session Details'}
            </SheetTitle>
            <SheetDescription>
              {sessionDetails?.session?.session_date} | {sessionDetails?.session?.start_time} - {sessionDetails?.session?.end_time}
            </SheetDescription>
          </SheetHeader>

          {loadingDetails ? (
            <div className="py-8 text-center text-muted-foreground">Loading...</div>
          ) : sessionDetails ? (
            <div className="mt-6">
              {/* Status and Actions */}
              <div className="flex items-center justify-between mb-4">
                <Badge className={`${STATUS_COLORS[sessionDetails.session?.status]} text-white`}>
                  {sessionDetails.session?.status}
                </Badge>
                <div className="flex gap-2">
                  {sessionDetails.session?.status === 'draft' && (
                    <>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleStatusTransition(sessionDetails.session.id, 'schedule')}
                      >
                        Schedule
                      </Button>
                      <Button
                        size="sm"
                        onClick={() => setShowCreateShifts(true)}
                        disabled={(sessionDetails.staff?.length || 0) === 0}
                      >
                        Create Shifts
                      </Button>
                    </>
                  )}
                  {sessionDetails.session?.status === 'scheduled' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusTransition(sessionDetails.session.id, 'start')}
                    >
                      <Play className="h-4 w-4 mr-1" />
                      Start Session
                    </Button>
                  )}
                  {sessionDetails.session?.status === 'active' && (
                    <Button
                      size="sm"
                      onClick={() => handleStatusTransition(sessionDetails.session.id, 'complete')}
                    >
                      <CheckCircle className="h-4 w-4 mr-1" />
                      Complete
                    </Button>
                  )}
                </div>
              </div>

              {/* Detail Tabs */}
              <Tabs value={detailTab} onValueChange={setDetailTab}>
                <TabsList className="w-full">
                  <TabsTrigger value="overview" className="flex-1">Overview</TabsTrigger>
                  <TabsTrigger value="participants" className="flex-1">
                    Participants ({sessionDetails.participants?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="staff" className="flex-1">
                    Staff ({sessionDetails.staff?.length || 0})
                  </TabsTrigger>
                  <TabsTrigger value="activities" className="flex-1">
                    Activities ({sessionDetails.activities?.length || 0})
                  </TabsTrigger>
                </TabsList>

                <TabsContent value="overview" className="mt-4 space-y-4">
                  <Card>
                    <CardContent className="pt-4 space-y-3">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <Label className="text-xs text-muted-foreground">Location</Label>
                          <p className="font-medium">{sessionDetails.session?.location_name || 'Not specified'}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Default Ratio</Label>
                          <p className="font-medium">{sessionDetails.session?.default_ratio}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Max Participants</Label>
                          <p className="font-medium">{sessionDetails.session?.max_participants}</p>
                        </div>
                        <div>
                          <Label className="text-xs text-muted-foreground">Required Staff</Label>
                          <p className="font-medium">{sessionDetails.session?.required_staff}</p>
                        </div>
                      </div>
                      {sessionDetails.session?.description && (
                        <div>
                          <Label className="text-xs text-muted-foreground">Description</Label>
                          <p className="text-sm">{sessionDetails.session.description}</p>
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </TabsContent>

                <TabsContent value="participants" className="mt-4">
                  <div className="flex justify-end mb-2">
                    <Button size="sm" onClick={() => setShowAddParticipant(true)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Add Participant
                    </Button>
                  </div>
                  <ScrollArea className="h-[400px]">
                    {(sessionDetails.participants?.length || 0) === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No participants added yet
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {sessionDetails.participants.map((p) => (
                          <Card key={p.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{p.participant_name}</p>
                                <Badge className={`${PARTICIPANT_STATUS_COLORS[p.status]} text-white text-xs`}>
                                  {p.status}
                                </Badge>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveParticipant(p.participant_id)}
                              >
                                <UserMinus className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="staff" className="mt-4">
                  <div className="flex justify-end mb-2">
                    <Button size="sm" onClick={() => setShowAddStaff(true)}>
                      <UserPlus className="h-4 w-4 mr-1" />
                      Assign Staff
                    </Button>
                  </div>
                  <ScrollArea className="h-[400px]">
                    {(sessionDetails.staff?.length || 0) === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No staff assigned yet
                      </div>
                    ) : (
                      <div className="space-y-2">
                        {sessionDetails.staff.map((s) => (
                          <Card key={s.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <div>
                                <p className="font-medium">{s.staff_name}</p>
                                <div className="flex items-center gap-2">
                                  <Badge className={`${ROLE_COLORS[s.role]} text-white text-xs`}>
                                    {s.role}
                                  </Badge>
                                  <span className="text-xs text-muted-foreground">
                                    {s.assigned_from} - {s.assigned_to}
                                  </span>
                                </div>
                              </div>
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => handleRemoveStaff(s.staff_id)}
                              >
                                <UserMinus className="h-4 w-4" />
                              </Button>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>

                <TabsContent value="activities" className="mt-4">
                  <div className="flex justify-end mb-2">
                    <Button size="sm" onClick={() => setShowAddActivity(true)}>
                      <Plus className="h-4 w-4 mr-1" />
                      Add Activity
                    </Button>
                  </div>
                  <ScrollArea className="h-[400px]">
                    {(sessionDetails.activities?.length || 0) === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        No activities added yet
                      </div>
                    ) : (
                      <div className="space-y-3">
                        {sessionDetails.activities.map((a) => (
                          <Card key={a.id} className="p-3">
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <Activity className="h-4 w-4 text-muted-foreground" />
                                  <p className="font-medium">{a.name}</p>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-muted-foreground mt-1">
                                  <span>{a.start_time} - {a.end_time}</span>
                                  <span>Ratio: {a.required_ratio}</span>
                                  <Badge variant="outline" className="text-xs">
                                    {a.participant_count || 0} participants
                                  </Badge>
                                </div>
                              </div>
                              <div className="flex items-center gap-1">
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleLinkAllToActivity(a.id)}
                                  title="Link all session participants"
                                >
                                  <Link2 className="h-4 w-4" />
                                </Button>
                                <Button
                                  size="sm"
                                  variant="ghost"
                                  onClick={() => handleDeleteActivity(a.id)}
                                >
                                  <Trash2 className="h-4 w-4" />
                                </Button>
                              </div>
                            </div>
                          </Card>
                        ))}
                      </div>
                    )}
                  </ScrollArea>
                </TabsContent>
              </Tabs>

            </div>
          ) : null}
        </SheetContent>
      </Sheet>

      {/* Add Participant Dialog */}
      <Dialog open={showAddParticipant} onOpenChange={setShowAddParticipant}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Participant</DialogTitle>
            <DialogDescription>Select a participant to add to this session</DialogDescription>
          </DialogHeader>
          <ScrollArea className="h-[300px]">
            <div className="space-y-2">
              {availableParticipants.map((p) => (
                <Button
                  key={p.id}
                  variant="ghost"
                  className="w-full justify-start"
                  onClick={() => handleAddParticipant(p.id)}
                >
                  {p.first_name} {p.last_name}
                </Button>
              ))}
            </div>
          </ScrollArea>
        </DialogContent>
      </Dialog>

      {/* Add Staff Dialog */}
      <Dialog open={showAddStaff} onOpenChange={setShowAddStaff}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Assign Staff</DialogTitle>
            <DialogDescription>Select a staff member and role</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <ScrollArea className="h-[200px]">
              <div className="space-y-2">
                {availableStaff.map((s) => (
                  <div key={s.id} className="flex items-center justify-between p-2 rounded hover:bg-muted">
                    <span>{s.full_name || s.email}</span>
                    <div className="flex gap-1">
                      <Button size="sm" variant="outline" onClick={() => handleAddStaff(s.id, 'support_worker')}>
                        Support
                      </Button>
                      <Button size="sm" variant="outline" onClick={() => handleAddStaff(s.id, 'lead')}>
                        Lead
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            </ScrollArea>
          </div>
        </DialogContent>
      </Dialog>

      {/* Add Activity Dialog */}
      <Dialog open={showAddActivity} onOpenChange={setShowAddActivity}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Activity</DialogTitle>
            <DialogDescription>Create an activity block for this session</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Activity Name *</Label>
              <Input
                value={activityForm.name}
                onChange={(e) => setActivityForm({ ...activityForm, name: e.target.value })}
                placeholder="e.g., Morning Exercise"
              />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Start Time *</Label>
                <Input
                  type="time"
                  value={activityForm.start_time}
                  onChange={(e) => setActivityForm({ ...activityForm, start_time: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label>End Time *</Label>
                <Input
                  type="time"
                  value={activityForm.end_time}
                  onChange={(e) => setActivityForm({ ...activityForm, end_time: e.target.value })}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Required Ratio</Label>
              <Select
                value={activityForm.required_ratio}
                onValueChange={(v) => setActivityForm({ ...activityForm, required_ratio: v })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {['1:1', '1:2', '1:3', '1:4', '1:5'].map((r) => (
                    <SelectItem key={r} value={r}>{r}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Location</Label>
              <Input
                value={activityForm.location}
                onChange={(e) => setActivityForm({ ...activityForm, location: e.target.value })}
                placeholder="e.g., Main Hall"
              />
            </div>
            <div className="space-y-2">
              <Label>Description</Label>
              <Textarea
                value={activityForm.description}
                onChange={(e) => setActivityForm({ ...activityForm, description: e.target.value })}
                placeholder="Optional description"
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddActivity(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleAddActivity}
              disabled={!activityForm.name || !activityForm.start_time || !activityForm.end_time}
            >
              Add Activity
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Create Shifts Dialog */}
      <Dialog open={showCreateShifts} onOpenChange={setShowCreateShifts}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Create Shifts from Session</DialogTitle>
            <DialogDescription>
              This will create one shift per assigned staff member, linked to this session.
              These shifts power attendance and timesheet generation.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Select Service *</Label>
              <LineItemSelect items={services} showRate={false} value={selectedServiceId} onChange={setSelectedServiceId}
                  placeholder="Select a service" testId="centre-session-service" />
            </div>
            <div className="bg-blue-50 p-3 rounded text-sm text-blue-700">
              <Info className="h-4 w-4 inline mr-1" />
              {sessionDetails?.staff?.length || 0} staff members assigned.
              {sessionDetails?.staff?.length || 0} shifts will be created.
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCreateShifts(false)}>
              Cancel
            </Button>
            <Button onClick={handleCreateShifts} disabled={!selectedServiceId}>
              Create Shifts
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
